import autobind from "autobind-decorator";
import { observer } from "mobx-react";
import * as React from "react";
import ReactDOM from "react-dom";
import styled, { css } from "styled-components";

import DomUtils from "@client/utils/DomUtils";
import Style from "@client/utils/Style";

import DropdownButton from "../DropdownButton";
import DropdownInput from "../DropdownInput";
import checkmarkImage from "./images/checkmark";
import tipImage from "./images/tip";

const getWidth = (props: any) => {
  if (props.large) {
    return Style.inputSizes.large.width - 2;
  }

  if (props.width) {
    return props.width - 2;
  }

  return Style.inputSizes.regular.width - 2;
};
const Wrapper = styled.div<{ embedded?: boolean }>`
  position: relative;
  ${props => (props.embedded ? "width: 100%;" : "")}
`;
export const List = styled.div`
  position: absolute;
  background: white;
  cursor: pointer;
  width: ${props => getWidth(props)}px;
  border: 1px solid ${Style.palette.grayscale[3]};
  border-radius: ${Style.borderRadius};
  z-index: 1000;
`;
export const ListItems = styled.div`
  max-height: 400px;
  overflow: auto;
`;
export const Tip = styled.div<{ primary?: boolean }>`
  position: absolute;
  width: 16px;
  height: 8px;
  right: 8px;
  top: -8px;
  z-index: 11;
  transition: all ${Style.animations.swift};
  overflow: hidden;
  svg {
    #path {
      transition: all ${Style.animations.swift};
      fill: ${(props: any) =>
        props.primary ? Style.palette.primaryHighlight : "white"};
    }
  }
`;
const Checkmark = styled.div<{ show: boolean }>`
  ${Style.exactWidth("16px")}
  height: 16px;
  margin-right: 8px;
  margin-top: 1px;
  display: flex;
  justify-content: center;
  align-items: center;
  #symbol {
    transition: stroke ${Style.animations.swift};
    stroke-dasharray: 12;
    stroke-dashoffset: ${props => (props.show ? 24 : 12)};
    animation-duration: 100ms;
    animation-timing-function: ease-in-out;
    animation-fill-mode: forwards;

    @keyframes dashOn {
      from {
        stroke-dashoffset: 12;
      }
      to {
        stroke-dashoffset: 24;
      }
    }
    @keyframes dashOff {
      from {
        stroke-dashoffset: 24;
      }
      to {
        stroke-dashoffset: 12;
      }
    }
  }
`;
type ListItemProps = {
  selected?: boolean;
  multipleSelected?: boolean;
  dim?: boolean;
  paddingLeft?: number;
};
export const ListItem = styled.div<ListItemProps>`
  position: relative;
  display: flex;
  color: ${props =>
    props.multipleSelected
      ? Style.palette.primaryHighlight
      : props.selected
        ? "white"
        : props.dim
          ? Style.palette.grayscale[3]
          : Style.palette.grayscale[4]};
  ${props =>
    props.selected
      ? css`
          background: ${Style.palette.primaryHighlight};
        `
      : ""}
  ${props =>
    props.selected
      ? css`
          font-weight: ${Style.fontWeights.medium};
        `
      : ""}
  padding: 8px 16px;
  transition: all ${Style.animations.swift};
  padding-left: ${(props: any) => props.paddingLeft}px;

  &:first-child {
    border-top-left-radius: ${Style.borderRadius};
    border-top-right-radius: ${Style.borderRadius};
  }

  &:last-child {
    border-bottom-left-radius: ${Style.borderRadius};
    border-bottom-right-radius: ${Style.borderRadius};
  }

  &:hover {
    background: ${Style.palette.primaryHighlight};
    color: white;
    ${Checkmark} #symbol {
      stroke: white;
    }
  }
`;
const DuplicatedLabel = styled.div`
  display: flex;
  font-size: 11px;
  span {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
`;
const Separator = styled.div`
  width: calc(100% - 32px);
  height: 1px;
  margin: 8px 16px;
  background: ${Style.palette.grayscale[3]};
`;
const Labels = styled.div`
  word-break: break-word;
  max-width: 100%;
`;

export const updateTipStyle = (
  listItemsRef: HTMLElement,
  tipRef: HTMLElement,
  firstItemRef: HTMLElement,
) => {
  if (tipRef && firstItemRef) {
    const svgPath = tipRef.querySelector("#path") as HTMLElement | null;
    if (!svgPath) {
      return;
    }
    if (listItemsRef.clientHeight < listItemsRef.scrollHeight) {
      svgPath.style.fill = "white";
      firstItemRef.style.borderTopRightRadius = "0";
    } else {
      svgPath.style.fill = "";
      firstItemRef.style.borderTopRightRadius = "";
    }
  }
};

export const scrollItemIntoView = (
  listRef: HTMLElement,
  listItemsRef: HTMLElement,
  itemIndex: number,
) => {
  if (!listRef || !listItemsRef) {
    return;
  }
  if (itemIndex === -1 || !listItemsRef.children[itemIndex]) {
    return;
  }
  const child = listItemsRef.children[itemIndex] as HTMLElement;
  const parent = child.parentNode as HTMLElement;
  parent.scrollTop = child.offsetTop - parent.offsetTop - 32;
};

type Props = {
  selectedItem: any;
  allowNew?: boolean | null;
  items: any[];
  labelField: string;
  valueField: string;
  className: string;
  newValue?: string | null;
  onNewValueChange?: (val: string) => void | null;
  onChange: (item: any) => void;
  noItemsMessage: string;
  noSelectionMessage?: string;
  large?: boolean;
  disabled: boolean;
  width: number;
  embedded?: boolean;
  dimFirstItem?: boolean;
  multipleSelection?: boolean;
  selectedItems?: string[];
  highlight?: boolean;
  itemRenderer?: ((item: any) => React.ReactNode) | null;
  selectedItemComponent?: React.ReactNode;
};
type State = {
  showDropdownList: boolean;
  firstItemHover: boolean;
};
@observer
class Dropdown extends React.Component<Props, State> {
  static defaultProps: Partial<Props> = {
    noSelectionMessage: "Select an item",
  };

  state: State = {
    showDropdownList: false,
    firstItemHover: false,
  };

  buttonRef: HTMLElement | null = null;
  listRef: HTMLElement | null = null;
  listItemsRef: HTMLElement | null = null;
  firstItemRef: HTMLElement | null = null;
  tipRef: HTMLElement | null = null;
  scrollableParent: HTMLElement | null = null;
  buttonRect: ClientRect | null = null;
  itemMouseDown = false;

  componentDidMount() {
    window.addEventListener("mousedown", this.handlePageClick, false);
    if (this.buttonRef) {
      this.scrollableParent = DomUtils.getScrollableParent(
        this.buttonRef,
      ) as HTMLElement;
      this.scrollableParent.addEventListener("scroll", this.handleScroll);
      window.addEventListener("resize", this.handleScroll);
      this.buttonRect =
        this.buttonRef && this.buttonRef.getBoundingClientRect();
    }
  }

  UNSAFE_componentWillUpdate() {
    if (this.buttonRef)
      this.buttonRect = this.buttonRef.getBoundingClientRect();
  }

  componentDidUpdate() {
    this.updateListPosition();
  }

  componentWillUnmount() {
    window.removeEventListener("mousedown", this.handlePageClick, false);
    window.removeEventListener("resize", this.handleScroll, false);
    this.scrollableParent &&
      this.scrollableParent.removeEventListener(
        "scroll",
        this.handleScroll,
        false,
      );
  }

  getLabel(item: any): string {
    const labelField = this.props.labelField || "label";

    if (item == null) {
      return this.props.noSelectionMessage || "";
    }

    if (item[labelField] != null) {
      return item[labelField].toString();
    }
    if (item.value != null) {
      return item.value.toString();
    }
    return item.toString();
  }

  getValue(item: any) {
    const valueField = this.props.valueField || "value";

    if (item == null) {
      return null;
    }

    return (
      (item[valueField] != null && item[valueField].toString()) ||
      this.getLabel(item)
    );
  }

  @autobind
  handleScroll() {
    if (this.buttonRef) {
      if (DomUtils.isElementInViewport(this.buttonRef, this.scrollableParent)) {
        this.buttonRect =
          this.buttonRef && this.buttonRef.getBoundingClientRect();
        this.updateListPosition();
      } else if (this.state.showDropdownList) {
        this.setState({ showDropdownList: false });
      }
    }
  }

  @autobind
  handlePageClick() {
    if (!this.itemMouseDown) {
      this.setState({ showDropdownList: false });
    }
  }

  handleButtonClick() {
    if (this.props.disabled) {
      return;
    }

    this.setState({ showDropdownList: !this.state.showDropdownList }, () => {
      this.scrollIntoView();
    });
  }

  handleItemClick(item: any, checkmarkRef: HTMLElement | null) {
    if (!this.props.multipleSelection) {
      this.setState({ showDropdownList: false, firstItemHover: false });
    } else if (checkmarkRef) {
      this.toggleCheckmarkAnimation(item, checkmarkRef);
    }

    if (this.props.onChange) {
      this.props.onChange(item);
    }
  }

  handleItemMouseEnter(index: number) {
    if (index === 0) {
      this.setState({ firstItemHover: true });
    }
  }

  handleItemMouseLeave(index: number) {
    if (index === 0) {
      this.setState({ firstItemHover: false });
    }
  }

  toggleCheckmarkAnimation(item: any, checkmarkRef: HTMLElement) {
    if (!item || !checkmarkRef) {
      return;
    }
    const multipleSelected =
      this.props.selectedItems &&
      this.props.selectedItems.find(i => i === this.getValue(item));
    const symbol = checkmarkRef.querySelector("#symbol") as HTMLElement | null;
    if (symbol) {
      symbol.style.animationName = multipleSelected ? "dashOff" : "dashOn";
    }
  }

  updateListPosition() {
    if (
      !this.state.showDropdownList ||
      !this.listRef ||
      !this.buttonRef ||
      !document.body ||
      !this.buttonRect ||
      !this.tipRef
    ) {
      return;
    }

    const buttonHeight = this.buttonRef.offsetHeight;
    const tipHeight = 8;
    let listTop = this.buttonRect.top + buttonHeight + tipHeight;
    const listHeight = this.listRef.offsetHeight;

    if (listTop + listHeight > window.innerHeight) {
      listTop = window.innerHeight - listHeight - 16;
      this.tipRef.style.display = "none";
    } else {
      this.tipRef.style.display = "block";
    }

    // If a modal is opened, body scroll is removed and body top is set to replicate scroll position
    let scrollOffset = 0;
    if (parseInt(document.body.style.top, 10) < 0) {
      scrollOffset = -parseInt(document.body && document.body.style.top, 10);
    }

    if (
      !this.listRef ||
      !this.buttonRef ||
      !this.buttonRect ||
      !this.tipRef ||
      !this.listItemsRef ||
      !this.firstItemRef
    ) {
      return;
    }
    const widthDiff = this.listRef.offsetWidth - this.buttonRef.offsetWidth;
    this.listRef.style.top = `${
      listTop + (window.pageYOffset || scrollOffset)
    }px`;
    this.listRef.style.left = `${
      this.buttonRect.left + window.pageXOffset - widthDiff
    }px`;
    updateTipStyle(this.listItemsRef, this.tipRef, this.firstItemRef);
  }

  scrollIntoView() {
    const itemIndex = this.props.items.findIndex(
      i => this.getValue(i) === this.getValue(this.props.selectedItem),
    );
    if (this.listRef && this.listItemsRef) {
      scrollItemIntoView(this.listRef, this.listItemsRef, itemIndex);
    }
  }

  renderLabel(
    label: string | null,
    value: string | null,
    duplicatedLabel: string | null,
  ) {
    return (
      <Labels>
        {label === "" ? "\u00A0" : label}
        {duplicatedLabel ? (
          <DuplicatedLabel>
            {" "}
            (<span>{value || ""}</span>)
          </DuplicatedLabel>
        ) : (
          ""
        )}
      </Labels>
    );
  }

  renderList() {
    if (
      !this.props.items ||
      this.props.items.length === 0 ||
      !this.state.showDropdownList
    ) {
      return null;
    }

    const body: any = document.body;
    const selectedValue = this.getValue(this.props.selectedItem);
    const duplicatedLabels: string[] = [];
    this.props.items.forEach((item, i) => {
      const label = this.getLabel(item);
      for (let j = i + 1; j < this.props.items.length; j += 1) {
        if (
          label === this.getLabel(this.props.items[j]) &&
          !duplicatedLabels.find(item2 => this.getLabel(item2) === label)
        ) {
          duplicatedLabels.push(label);
        }
      }
    });
    const firstItemValue =
      this.props.items.length > 0 ? this.getValue(this.props.items[0]) : null;
    const isFirstItemSelected = selectedValue === firstItemValue;

    const { onNewValueChange, ...props } = this.props;

    const list = ReactDOM.createPortal(
      <List
        {...props}
        ref={ref => {
          this.listRef = ref;
        }}
      >
        <Tip
          ref={ref => {
            this.tipRef = ref;
          }}
          primary={this.state.firstItemHover || isFirstItemSelected}
          dangerouslySetInnerHTML={{ __html: tipImage }}
        />
        <ListItems
          ref={ref => {
            this.listItemsRef = ref;
          }}
        >
          {this.props.items.map((item, i) => {
            if (item.separator === true) {
              return <Separator key={i} />;
            }

            const label = this.getLabel(item);
            const value = this.getValue(item);
            const duplicatedLabel = duplicatedLabels.find(l => l === label);
            const multipleSelected =
              this.props.selectedItems &&
              this.props.selectedItems.find(i => i === value);
            let checkmarkRef: HTMLElement | null = null;
            const listItem = (
              <ListItem
                ref={ref => {
                  if (i === 0) {
                    this.firstItemRef = ref;
                  }
                }}
                key={value}
                onMouseDown={() => {
                  this.itemMouseDown = true;
                }}
                onMouseUp={() => {
                  this.itemMouseDown = false;
                }}
                onMouseEnter={() => {
                  this.handleItemMouseEnter(i);
                }}
                onMouseLeave={() => {
                  this.handleItemMouseLeave(i);
                }}
                onClick={() => {
                  this.handleItemClick(item, checkmarkRef);
                }}
                selected={
                  !this.props.multipleSelection && value === selectedValue
                }
                multipleSelected={Boolean(
                  this.props.multipleSelection && multipleSelected,
                )}
                dim={this.props.dimFirstItem && i === 0}
                paddingLeft={this.props.multipleSelection ? 8 : 16}
              >
                {this.props.multipleSelection ? (
                  <Checkmark
                    ref={ref => {
                      checkmarkRef = ref;
                    }}
                    dangerouslySetInnerHTML={{ __html: checkmarkImage }}
                    show={Boolean(multipleSelected)}
                  />
                ) : null}
                {this.props.itemRenderer
                  ? this.props.itemRenderer(item)
                  : this.renderLabel(label, value, duplicatedLabel || null)}
              </ListItem>
            );

            return listItem;
          })}
        </ListItems>
      </List>,
      body,
    );

    return list;
  }

  render() {
    const buttonValue = () => {
      if (this.props.items && this.props.items.length) {
        if (
          this.props.multipleSelection &&
          this.props.selectedItems &&
          this.props.selectedItems.length > 0
        ) {
          return this.props.selectedItems
            .map(i =>
              this.getLabel(
                this.props.items.find(item => this.getValue(item) === i),
              ),
            )
            .join(", ");
        }
        return this.getLabel(this.props.selectedItem);
      }

      return this.props.noItemsMessage || "";
    };
    const { onNewValueChange, ...props } = this.props;

    return (
      <Wrapper
        className={this.props.className}
        onMouseDown={() => {
          this.itemMouseDown = true;
        }}
        onMouseUp={() => {
          this.itemMouseDown = false;
        }}
        embedded={this.props.embedded}
      >
        {this.props.allowNew &&
        this.props.selectedItem &&
        this.props.selectedItem.value === "__addnew__" ? (
          <DropdownInput
            {...props}
            value={this.props.newValue || null}
            customRef={(ref: any) => {
              this.buttonRef = ref;
            }}
            onChange={val => {
              if (onNewValueChange) {
                onNewValueChange(val);
              }
            }}
            onMouseDown={() => {
              this.itemMouseDown = true;
            }}
            onMouseUp={() => {
              this.itemMouseDown = false;
            }}
            onClick={() => this.handleButtonClick()}
          />
        ) : (
          <DropdownButton
            {...props}
            valueComponent={this.props.selectedItemComponent}
            customRef={(ref: any) => {
              this.buttonRef = ref;
            }}
            // @ts-ignore
            onMouseDown={() => {
              this.itemMouseDown = true;
            }}
            onMouseUp={() => {
              this.itemMouseDown = false;
            }}
            value={buttonValue()}
            onClick={() => this.handleButtonClick()}
            selected={this.state.showDropdownList}
          />
        )}

        {this.renderList()}
      </Wrapper>
    );
  }
}

export default Dropdown;
